"use client";
import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";

import styles from "./index.module.css";
import { useClient, useToken } from "../../lib/context/auth-context";
import { getActiveCart, updateCart } from "../slice/cart-slice";
import { useAppDispatch, useAppSelector } from "../slice/hooks";
import { useHistoryManager } from "../history/useHistoryManager";

import {
  buildQueryString,
  extractInfoFromUrl,
  useActiveCart,
} from "../../lib/util";

import { emptyState } from "../../assets";

import { SkeletonCard } from "..";
import StoreBox from "./store-box";
import ProductItem from "../product/product-card";
import CategoryList from "./search-category";
import CoverPage from "./cover-page";
import FloatingHelper from "./floating-helper";
import Pagination from "../common/Pagination";
import { setAccessPointId } from "../slice/accessOrg-slice";
import {
  getActiveCategory,
  getCategories,
  setActiveCategory,
  setCategories,
} from "../slice/category-slice";

function StorePage(props) {
  const {
    modal,
    showProduct,
    showCart,
    accessPointId,
    searchTerm,
    setSearchTerm,
    storePath,
    organisation,
    accessPoint,
    categories,
    serverToken,
  } = props;

  const disableStoreProductPrice =
    accessPoint?.settings?.disableStoreFrontPrice || false;

  const { token } = useToken();
  const client = useClient();
  const dispatch = useAppDispatch();

  const searchBarRef = useRef(null);

  const { navigateTo } = useHistoryManager(storePath);
  const [query, setQuery] = useState("");

  // const [categories, setCategories] = useState(initialCategories);

  const [isShowingFloater, setIsShowingFloater] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);

  const cart = useAppSelector(getActiveCart);

  const { isLoading: isCartLoading, mutate: mutateActiveCart } = useActiveCart({
    onError: (response) => {
      // setError(response.message);
    },
    onSuccess: async (response) => {
      const cart = response.data.collection;
      dispatch(updateCart(cart.profileId, cart.accessPointId, cart));
    },
  });

  const [state, setState] = useState({
    data: [],
    limit: 8,
    total: 0,
    currentPage: 1,
    page: 1,
    search: "",
  });

  // const [activeCategory, setActiveCategory] = useState(null);
  const activeCategory = useAppSelector((state) => getActiveCategory(state));

  const queryKey = useMemo(
    () => ["feeds", activeCategory, query, state.page, accessPointId],
    [activeCategory, query, state.page, accessPointId]
  );

  const {
    data: feeds = [],
    isLoading,
    isFetching,
  } = useQuery(queryKey, () => {
    const queryParams = [];
    if (activeCategory) {
      queryParams.push(`productCategoryId=${activeCategory}`);
    }
    if (query) {
      queryParams.push(`searchTerm=${query}`);
    }

    queryParams.push(`limit=${state.limit}`);
    queryParams.push(`page=${state.page}`);
    queryParams.push(`accessPointId=${accessPointId}`);

    const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";

    return client(
      `product/public/organisation/${organisation?.orgId}/feeds${queryString}`,
      {
        headers: {
          Authorization: `Bearer ${token ? token : serverToken}`,
        },
      }
    ).then((i) => i.data);
  });

  const { data: _categories = [], isLoading: isCategoryLoading } = useQuery(
    ["_categories", accessPointId, organisation?.orgId],
    () => {
      return client(
        `product/public/category/organisation/${organisation?.orgId}/feeds?accessPointId=${accessPointId}`,
        {
          headers: {
            Authorization: `Bearer ${token ? token : serverToken}`,
          },
        }
      ).then((i) => i.data.productCategories);
    }
  );

  const storeInState = () => {
    let q = extractInfoFromUrl("q") || "";
    q = q.trim();
    const query = q ? buildQueryString({ q }) : "";
    navigateTo(`${window.location.pathname}${query}`, {
      replace: true,
    });
  };

  const handleLocationChange = (e) => {
    if (e.target.value === accessPointId) return null;
    activateLocation(e.target.value);
  };

  const resetPage = (resetCategory = true, persistQuery = true) => {
    if (resetCategory) dispatch(setActiveCategory(""));
    if (persistQuery) storeInState();
    setState((prev) => ({
      ...prev,
      data: [],
      total: 0,
      currentPage: 1,
      page: 1,
    }));
  };

  const openPage = (page) => {
    setState((prev) => ({
      ...prev,
      page,
    }));
  };

  const switchCategory = (id) => {
    resetPage();
    // setActiveCategory(id);
    dispatch(setActiveCategory(id));
  };

  const activateSearch = (value = null) => {
    const rawValue = value !== null ? value : searchTerm || "";
    const trimmed = rawValue.trim();
    if (query.trim() == trimmed) return;

    // Reset pagination state before searching
    resetPage(false, false);

    // Store the active query
    setQuery(trimmed);

    const urlQuery = buildQueryString({ q: trimmed });
    navigateTo(`${window.location.pathname}${urlQuery}`, { replace: true });
  };

  const activateLocation = (value) => {
    resetPage();
    dispatch(setAccessPointId(value));
    mutateActiveCart({
      orgId: organisation?.orgId,
      accessPointId: value,
    });
  };

  useEffect(() => {
    if (feeds && feeds.products) {
      const { products, limit, total, currentPage } = feeds;
      setState((prev) => ({
        ...prev,
        data: [...(prev.data || []), ...products],
        limit,
        total,
        currentPage,
      }));
    }
  }, [feeds]);

  useEffect(() => {
    if (_categories && _categories.length > 0) {
      // setCategories(_categories);
      dispatch(setCategories(_categories));
    }
  }, [_categories]);

  const handleSearchTerm = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  useEffect(() => {
    const q = extractInfoFromUrl("q");
    if (!q) return;
    setSearchTerm(q);
  }, []);

  useEffect(() => {
    if (searchTerm.trim() !== "") {
      setHasTyped(true);
    }

    const delayDebounce = setTimeout(() => {
      activateSearch();
    }, 1000); // wait 300ms after typing stops

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  return (
    <StoreBox
      {...props}
      handleLocationChange={handleLocationChange}
      categories={categories}
      switchCategory={switchCategory}
      activeCategory={activeCategory}
      setActiveCategory={setActiveCategory}
      showSearchBar={searchTerm != "" ? true : false}
      headerClassName="no-top-bottom-margin"
      isHomePage={true}
      searchBarRef={searchBarRef}
      hasTyped={hasTyped}
      setHasTyped={setHasTyped}
    >
      <>
        {searchTerm === "" && hasTyped == false && (
          <CoverPage
            organisation={organisation}
            searchTerm={searchTerm}
            activateSearch={activateSearch}
            handleSearchTerm={handleSearchTerm}
            searchBarRef={searchBarRef}
          />
        )}

        <div className={styles.content}>
          <div className={styles.categoryPad}>
            <CategoryList
              categories={categories}
              activeCategory={activeCategory}
              handleCategoryChange={(id) => switchCategory(id)}
            />
          </div>

          <div className={styles.cardWrapper}>
            {state.data && !isLoading && !state.data.length && (
              <div className={styles.emptystate}>
                <p>We could not find any product based on your search.</p>
                <div>{emptyState}</div>
              </div>
            )}

            {state.data &&
              state.data.map((i, idx) => (
                <ProductItem
                  product={i}
                  key={idx}
                  showProduct={showProduct}
                  disableStoreProductPrice={disableStoreProductPrice}
                  accessPoint={accessPoint}
                />
              ))}

            {isLoading && state.currentPage == 1 && state.total == 0 && (
              <SkeletonCard />
            )}
          </div>

          <Pagination
            infiniteScroll={true}
            total={state.total}
            limit={state.limit}
            page={state.page}
            onPageClick={openPage}
            enableManualMode={true}
            autoLoadThreshold={5}
            isLoading={isLoading}
            isFetching={isFetching}
            showEndOfList={true}
            endOfListText="🎉 You've reached the end!"
          />

          <FloatingHelper
            modal={modal}
            showCart={showCart}
            cart={cart}
            setIsShowingFloater={setIsShowingFloater}
          />
        </div>
      </>
    </StoreBox>
  );
}

export default StorePage;
